export interface RemoteDeviceDto {
  deviceId: string
  defaultName?: string | null
  displayName?: string | null
  name?: string | null
  online?: boolean
  connectionCount?: number
  lastSeenAt?: string | null
  [key: string]: unknown
}

export interface RemoteDeviceView {
  deviceId: string
  name: string
  online: boolean
  connectionCount: number
  lastSeenAt?: string | null
  raw: RemoteDeviceDto
}

export interface RemoteConnectTicket {
  ticket: string
  signalingUrl: string
  expiresAt: number
  deviceId: string
}

function readRemoteError(payload: unknown, fallback: string): string {
  if (typeof payload === 'object' && payload !== null && 'error' in payload) {
    const error = (payload as { error?: unknown }).error
    if (typeof error === 'string' && error.trim()) return error.trim()
  }
  return fallback
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

async function requireOkJson(response: Response, fallback: string): Promise<unknown> {
  const payload = await readJson(response)
  if (response.ok) return payload
  throw new Error(response.status === 401 ? 'UNAUTHENTICATED' : readRemoteError(payload, fallback))
}

export function normalizeRemoteDevice(device: RemoteDeviceDto): RemoteDeviceView {
  const name = device.displayName || device.name || device.defaultName || device.deviceId
  return {
    deviceId: device.deviceId,
    name,
    online: device.online === true,
    connectionCount: typeof device.connectionCount === 'number' && Number.isFinite(device.connectionCount)
      ? device.connectionCount
      : 0,
    lastSeenAt: device.lastSeenAt,
    raw: device,
  }
}

export function buildDeviceConnectUrl(deviceId: string): string {
  return `/api/devices/${encodeURIComponent(deviceId)}/connect`
}

export async function fetchMyDevices(): Promise<RemoteDeviceView[]> {
  const response = await fetch('/api/my-devices', { credentials: 'include' })
  const payload = await requireOkJson(response, 'REMOTE_DEVICES_FAILED')
  const devices = typeof payload === 'object' && payload !== null && Array.isArray((payload as { devices?: unknown }).devices)
    ? (payload as { devices: RemoteDeviceDto[] }).devices
    : []
  return devices.map(normalizeRemoteDevice)
}

export async function connectDevice(deviceId: string): Promise<RemoteConnectTicket> {
  const response = await fetch(buildDeviceConnectUrl(deviceId), {
    method: 'POST',
    credentials: 'include',
  })
  const payload = await requireOkJson(response, 'REMOTE_CONNECT_FAILED')
  if (typeof payload !== 'object' || payload === null) {
    throw new Error('REMOTE_CONNECT_FAILED')
  }
  const ticket = (payload as { ticket?: unknown }).ticket
  const signalingUrl = (payload as { signalingUrl?: unknown }).signalingUrl
  const expiresAt = (payload as { expiresAt?: unknown }).expiresAt
  const returnedDeviceId = (payload as { deviceId?: unknown }).deviceId
  if (
    typeof ticket !== 'string'
    || typeof signalingUrl !== 'string'
    || typeof expiresAt !== 'number'
    || !Number.isFinite(expiresAt)
    || typeof returnedDeviceId !== 'string'
  ) {
    throw new Error('REMOTE_CONNECT_FAILED')
  }
  return { ticket, signalingUrl, expiresAt, deviceId: returnedDeviceId }
}

export async function renameDevice(deviceId: string, displayName: string): Promise<RemoteDeviceView> {
  const response = await fetch(`/api/devices/${encodeURIComponent(deviceId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  })
  const payload = await requireOkJson(response, 'REMOTE_RENAME_FAILED')
  const device = typeof payload === 'object' && payload !== null
    ? (payload as { device?: RemoteDeviceDto }).device
    : null
  if (!device) throw new Error('REMOTE_RENAME_FAILED')
  return normalizeRemoteDevice(device)
}

export async function deleteDevice(deviceId: string): Promise<void> {
  const response = await fetch(`/api/devices/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  await requireOkJson(response, 'REMOTE_DELETE_FAILED')
}
